<template>
  <div id="dataset-obs4-modal">
    <modal v-model="show"
           :footer="false"
           :appendToBody="appendToBody"
           ref="modal"
           size="lg">
      <template slot="title">
        <span v-html="modalTitle"></span>
      </template>
      <h3>Informations</h3>
      <alert type="info" v-if="target == null">
        <p class="text-justify">
          <strong>
            Merci de renseigner la (ou les) période(s) de l'observation
            des pratiques «{{name}}», puis de cliquer sur
            «Enregistrer les informations».
          </strong>
        </p>
      </alert>
      <div class="form-group"
           :class="{ 'has-error': fields.name.error }">
        <label for="name" class="control-label">
          Nom
        </label>
        <input required
               id="name"
               class="form-control"
               :disabled="nameDisabled"
               v-model.trim="fields.name.value"
               @blur="checkName"
               type="text"
               placeholder="Nom"/>
        <span class="text-danger help-block">{{ fields.name.error }}</span>
      </div>
      <div class="form-group">
        <label class="control-label">
          Période d'observation des pratiques
        </label>
        <div v-for="(p, k) in fields.periods"
             :key="`period-${k}`" class="panel panel-default">
          <div class="panel-body">
            <label class="control-label" v-if="p.name">
              {{p.name}}
            </label>
            <sui-dataset-period v-model="p.value"
                                :limits="periodLimits"
                                :defaults="periodDefaults"
                                hide-two-periods>
            </sui-dataset-period>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12">
            <sui-error-panel :error="error.info" v-if="error.info.error">
            </sui-error-panel>
            <p>
              <btn type="primary" size="lg"
                   :disabled="saveDisabled" @click="save">
                Enregistrer les informations
              </btn>
            </p>
          </div>
        </div>
      </div>
      <div v-if="versionDiff">
        <hr />
        <h3>Validation et envoi des fiches</h3>
        <div v-if="periodSheets.length === 0">
          <div v-if="target.step > 0">
            <alert type="success" >
              <p class="text-justify">
                <strong>
                  Aucune fiche n'a été retrouvée pour cette modalité.
                </strong>
              </p>
            </alert>
          </div>
          <div v-else>
            <alert type="warning">
              <p class="text-justify">
                <strong>
                  Il n'y a aucune fiche sur la période sélectionnée.
                </strong>
              </p>
              <p class="text-justify">
                Merci de renseigner des fiches d'observation dans le module de saisie puis de les valider.
                Si des fiches ont été saisies, veuillez sélectionner une période cohérente avec les dates de vos observations.
              </p>
            </alert>
          </div>
        </div>
        <div v-else>
          <div v-if="versionDiff.diffCount === 0" >
            <alert v-if="periodSheets.length > 0" type="success">
              <p>
                <strong>
                  Les données «{{name}}» contiennent {{periodSheets.length}}
                  fiche.s.
                </strong>
              </p>
              <p>
                Toutes les fiches sont à jour.
              </p>
              <p>
                <btn size="sm" type="default"
                     @click="sheetNumbers = !sheetNumbers">
                  {{sheetNumbers ? 'Masquer' : 'Voir'}} la liste
                </btn>
              </p>
              <collapse v-model="sheetNumbers">
                <table class="table table-responsive">
                  <thead>
                    <tr>
                      <th>Numéro</th>
                      <th>Type</th>
                      <th>Service</th>
                      <th>Uf</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, k) in displayPeriodSheets"
                        :key="`valide-sheet-${k}`">
                      <td>{{p.sheetNumber}}</td>
                      <td>{{p.type}}</td>
                      <td>{{p.unitType}}</td>
                      <td>{{p.uf}}</td>
                    </tr>
                  </tbody>
                </table>
              </collapse>
            </alert>
            <alert v-else type="warning">
              Aucune fiche saisie.
            </alert>
          </div>
          <div v-else>
            <div v-if="!iterator.started">
              <alert type="warning" class="text-justify"
                     v-if="dsSheets == null || dsSheets.length === 0">
                <p>
                  <strong>
                    Aucune fiche n'a été envoyée pour les données «{{name}}»,
                    mais il y a {{periodSheets.length}} fiche.s correspondant à
                    la période surveillée.
                  </strong>
                </p>
                <p>
                  Utilisez le bouton ci-dessous pour valider et envoyer
                  automatiquement les fiches.
                </p>
              </alert>
              <alert type="warning" class="text-justify" v-else>
                <p>
                  <strong>
                    Les données «{{name}}» contiennent actuellement
                    {{periodSheets.length}} fiche.s.
                  </strong>
                </p>
                <p>
                  Nous avons cependant détecté des modifications sur les fiches
                  depuis le dernier envoi :
                  <ul>
                    <li v-if="versionDiff.v.length > 0">
                      {{ versionDiff.v.length }} fiche.s modifiée.s
                    </li>
                    <li v-if="versionDiff.a.length > 0">
                      {{versionDiff.a.length}} fiche.s ajoutée.s
                    </li>
                    <li v-if="versionDiff.d.length > 0">
                      {{versionDiff.d.length}} fiche.s supprimée.s
                    </li>
                  </ul>
                  <strong>
                    Pensez à mettre à jour les fiches envoyées pour les données
                    «{{name}}» en utilisant le bouton ci-dessous.
                  </strong>
                </p>
              </alert>
              <sui-error-panel :error="error.sheets" v-if="error.sheets.error">
              </sui-error-panel>
              <p>
                <btn @click="updateSheets" type="primary" size="lg">
                  Mettre à jour les fiches pour «{{ name }}»
                </btn>
              </p>
            </div>
            <div v-else>
              <steps :steps="iterator.steps" :stepStates="iterator.stepStates"></steps>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>
<script>
import suiErrorPanel from '@/sui/components/errorpanel'
import steps from '@/sui/components/steps'
import period from '@/sui/components/dataset/period'
import checkbox from '@/sui/components/checkbox'

import modules from '@/sui/data/modules'

import StepIterator from '@/sui/tools/stepiterator'
import Error from '@/sui/tools/error'

import impl from '@/sui/components/impl/dataset'

import moment from 'moment'

export default {
  name: 'dataset-modal',
  props: {
    value: {
      type: Boolean,
      required: true
    },

    surveillance: {
      type: Object,
      required: true
    },

    name: {
      type: String
    },

    module: {
      type: String,
      required: true
    },

    appendToBody: {
      type: Boolean,
      default: false
    },

    sheets: {
      type: Array,
      default: () => []
    }
  },

  components: {
    'sui-error-panel': suiErrorPanel,
    'sui-dataset-period': period,
    'steps': steps,
    'c-checkbox': checkbox
  },

  data: function () {
    return {
      show: this.value,
      sheetNumbers: false,
      error: {
        info: new Error(),
        sheets: new Error()
      },
      waiting: false,
      iterator: new StepIterator(impl.steps),
      dsSheets: null,
      periodSheets: null,
      periodSheetsAsked: false,
      fields: {
        name: { value: this.name, error: '' },
        periods: []
      },
      filled: false
    }
  },

  watch: {
    name: function () {
      this.filled = false
    },

    value: function () {
      if (this.value && !this.filled) {
        this.fill()
      }
      this.show = this.value
    },

    show: function (s) {
      if (!s) {
        if (this.iterator.completed) {
          const request = {
            t: this.module,
            s: this.surveillance.id,
            y: this.surveillance.year
          }
          this.$store.dispatch('sheet/load', request)
        }
      }
      this.$emit('input', this.show)
    },

    surveillance: function (s) {
      this.refill()
    },

    module: function () {
      this.refill()
    }
  },

  mounted: function () {
    this.refill()
  },

  computed: {
    periodLimits: function () {
      const spiadi = this.name.toLowerCase().includes('spiadi')
      const y = this.surveillance.year
      return spiadi
        ? impl.mainObservationPeriodLimits(y)
        : impl.customPeriodLimits(y)
    },

    periodDefaults: function () {
      const y = this.surveillance.year
      return {
        start: `${y}-01-01`,
        end: `${y}-04-01`
      }
    },

    displayPeriodSheets: function () {
      const year = `${this.surveillance.year}`
      const mod = modules[year][this.module]
      return impl.makePeriodSheetsList(year, this.periodSheets, mod)
    },

    periods: function () {
      return impl.toServerPeriods(this.fields.periods)
    },

    moduleName: function () {
      return modules[`${this.surveillance.year}`][this.module].label
    },

    versionDiff: function () {
      const t = this.target
      if (t == null || !this.periodSheets || !this.dsSheets) {
        return null
      }

      return impl.versionDiff(this.dsSheets, this.periodSheets)
    },

    saveDisabled: function () {
      let same = this.target && this.periods.length === this.target.peri.length
      if (same) {
        const pred = (f, s) => {
          const unit = f.unit.localeCompare(s.unit)
          if (unit !== 0) {
            return unit
          }

          const start = moment(f.start).valueOf() - moment(s.start).valueOf()
          if (start !== 0) {
            return start
          }

          return moment(f.end).valueOf() - moment(s.end).valueOf()
        }
        const tp = this.target.peri.slice().sort(pred)
        const p = this.periods.slice().sort(pred)
        for (let i in tp) {
          if (pred(tp[i], p[i]) !== 0) {
            same = false
            break
          }
        }
      }
      return this.waiting || this.hasError() || same
    },

    modalTitle: function () {
      const e = this.surveillance.establishment.name
      const m = this.moduleName
      const l = `label-${this.module}`
      return `${e}<br>Clôture <span class='label ${l}'>${m}</span> pour «${this.name}»`
    },

    nameDisabled: function () {
      return this.name != null || this.target != null
    },

    target: function () {
      if (this.surveillance == null) {
        return null
      }

      const mod = this.surveillance.modules[this.module]
      if (mod.dset == null) {
        return null
      }

      return mod.dset.find(
        d => d.name === this.fields.name.value
      )
    },

    reference: function () {
      if (this.surveillance == null) {
        return null
      }

      return modules.get(this.surveillance.year)[this.module].sheets.edit
    }
  },

  methods: {
    hasError: function () {
      if (this.fields.name.error !== '') {
        return true
      }

      for (let i in this.fields.periods) {
        const p = this.fields.periods[i]
        if (p.value.date.error !== '' ||
            p.value.date2.error !== '') {
          return true
        }
      }
      return false
    },

    refill: function () {
      if (this.value) {
        this.fill()
      } else {
        this.filled = false
      }
    },

    updateSheets: function () {
      this.iterator.reset()
      this.iterator.start(this)
    },

    save: function () {
      const f = this.fields
      const periods = this.periods

      this.waiting = true
      const vm = this
      this.$store.dispatch('surveillances/updateDataset', {
        name: f.name.value,
        mod: this.module,
        surv: this.surveillance.id,
        peri: periods,
        year: this.surveillance.year
      }).then(
        r => {
          const err = r.data.err
          if (err) {
            vm.waiting = false
            this.error.info.set(
              'Erreur interne !',
              `Une erreur inattendue s'est produite lors de l'enregistrement
du jeu de données.`,
              `Merci de vérifier votre connexion internet avant de réessayer.`
            )
          } else {
            vm.$notify(
              {
                type: 'success',
                title: 'Enregistrement réussi !',
                content: `Si vous avez modifié les dates, il vous faut mettre
à jour les fiches.`,
                duration: 4000
              }
            )
          }
        }
      ).catch(
        e => {
          vm.waiting = false
          this.error.info.set(
            'Erreur interne !',
            `Une erreur inattendue s'est produite lors de l'enregistrement
du jeu de données.`,
            `Merci de vérifier votre connexion internet avant de réessayer.`
          )
        }
      )
    },

    checkName: function () {
      this.fields.name.error = ''
      if (this.fields.name === '') {
        this.fields.name.error =
          'Le nom du jeu de données ne doit pas être vide.'
      }
    },

    setPeriodsByUf: function () {
      this.fields.periods.length = 0
      const year = this.surveillance.year
      this.fields.periods.push(
        {
          value: {
            unit: '',
            twoPeriods: false,
            date: { start: `${year}-01-01`, end: `${year}-04-01`, error: '' },
            date2: { start: `${year}-01-01`, end: `${year}-04-01`, error: '' }
          }
        }
      )
    },

    getPeriodSheets: function () {
      const vm = this
      const list = []
      vm.periods.forEach(
        p => {
          list.push(
            vm.$store.dispatch(
              'sheet/fromPeriod',
              {
                type: vm.module,
                surv: vm.surveillance.id,
                year: vm.surveillance.year,
                peri: p
              }
            )
          )
        }
      )

      this.periodSheetsAsked = true
      Promise.all(list).then(
        (res) => {
          const sheets = []
          for (let i in res) {
            const r = res[i]
            if (r.data.err != null) {
              return res
            }
            sheets.push(...r.data.v)
          }

          const result = []
          const ids = {}
          sheets.forEach(
            s => {
              if (ids[s.id] == null) {
                result.push(s)
                ids[s.id] = true
              }
            }
          )
          vm.periodSheets = result

          return res
        }
      )
    },

    fill: function () {
      this.dsSheets = null
      this.dsDenominators = null
      this.periodSheets = null
      this.periodSheetsAsked = false
      this.iterator.reset()
      this.waiting = false
      this.error.info.reset()
      this.error.sheets.reset()
      this.emptyDiscList = []
      this.selectedUfList = []
      this.denominatorsUfList = []
      const { name } = this.fields
      name.error = ''
      name.value = this.name
      this.filled = true

      if (this.target == null) {
        this.setPeriodsByUf()
        return
      }

      this.fields.name.value = this.target.name

      const vm = this
      const arg = {
        mod: vm.module,
        surv: vm.surveillance.id,
        name: vm.fields.name.value,
        year: vm.surveillance.year
      }

      const promises = [
        vm.$store.dispatch('surveillances/getDatasetSheets', arg)
      ]

      Promise.all(promises).then(
        (res) => {
          const sheets = res[0]
          if (sheets.data.err != null) {
            vm.error.info.set(
              'Erreur interne !',
              `Une erreur inattendue s'est produite lors de la récupération
des fiches.`,
              `Merci de vérifier votre connexion internet avant de réessayer.`
            )
            return res
          }

          vm.dsSheets = sheets.data.v
          return res
        }
      ).catch(
        e => {
          vm.error.info.set(
            'Erreur interne !',
            `Une erreur inattendue s'est produite lors de la récupération
des fiches.`,
            `Merci de vérifier votre connexion internet avant de réessayer.`
          )
        }
      )

      const p = vm.target.peri
      if (p.length > 0 && (p[0].unit == null || p[0].unit === '')) {
        vm.fields.periods.length = 0
        vm.fields.periods.push(
          { value: impl.buildPeriod(vm.surveillance.year, p, '') }
        )
        this.getPeriodSheets()
      }
    }
  }
}
</script>
<style lang="less" scoped>
@import '~variables';

.alert h4 {
  color: white;
}

</style>
